import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

type TextPart = Extract<ChatPart, { kind: 'text' }>;
type ToolCallPart = Extract<ChatPart, { kind: 'tool-call' }>;
type ToolResultPart = Extract<ChatPart, { kind: 'tool-result' }>;
type RenderComponentPart = Extract<ChatPart, { kind: 'render-component' }>;
type ProposeActionPart = Extract<ChatPart, { kind: 'propose-action' }>;
type ActionExecutedPart = Extract<ChatPart, { kind: 'action-executed' }>;

const SUB_AGENT_TOOL_NAME_PREFIX = 'agent-';

/** Search/load plumbing, not user-facing work — hidden from the timeline. */
const HIDDEN_META_TOOL_NAMES = new Set(['search_tools', 'load_tool']);

/** Matches a `{{component:<id>}}` token. Lenient on the id so markdown punctuation isn't trapped. */
const COMPONENT_TOKEN_REGEX = /\{\{component:([a-zA-Z0-9_]+)\}\}/g;

/** One row in the thinking-section timeline for an assistant message. */
export interface TimelineToolEvent {
  kind: 'tool';
  toolCallId: string;
  name: string;
  status: ToolCallPart['status'];
  /** Provenance — non-empty when this row came from a flattened `agent-*` sub-agent. */
  subAgentToolName?: string;
}

/** Reasoning row in the thinking-section timeline (rendered via the text-part component). */
export interface TimelineReasoningEvent {
  kind: 'reasoning';
  messageId: string;
  content: string;
  done: boolean;
  /** Provenance — non-empty when this row came from a flattened `agent-*` sub-agent. */
  subAgentToolName?: string;
}

/** Discriminated union of all thinking-section row kinds. */
export type TimelineEvent = TimelineToolEvent | TimelineReasoningEvent;

/**
 * One entry in the assistant message body. Layout follows `{{component:<id>}}` tokens in the
 * prose, not tool-call order: text splits around tokens, each token expands to a `render-tool`.
 * Unreferenced render calls don't appear; one that errored before a componentId becomes a
 * `render-error` pill.
 */
export type BodyEntry =
  | { kind: 'text'; key: string; content: string; done: boolean }
  | { kind: 'render-tool'; key: string; part: ToolCallPart }
  | { kind: 'render-error'; key: string; toolName: string }
  | { kind: 'propose-action'; key: string; part: ProposeActionPart }
  | { kind: 'action-executed'; key: string; part: ActionExecutedPart };

/** Split of an assistant `ChatMessage` into timeline rows + body entries. */
export interface AssistantMessageView {
  timeline: TimelineEvent[];
  body: BodyEntry[];
}

export interface SplitOptions {
  /** When true, sub-agent (`agent-*`) inner steps surface inside the parent thinking timeline. */
  subagentAsThinking?: boolean;
}

/**
 * Split an assistant message into a thinking timeline + a renderable body.
 * `renderToolNames` are the tool ids with a registered FE presenter (eligible for the body);
 * every other tool-call becomes a timeline row.
 */

export const splitAssistantMessage = (
  msg: ChatMessage,
  renderToolNames: ReadonlySet<string>,
  options: SplitOptions = {},
): AssistantMessageView => {
  if (msg.role !== 'assistant') return { timeline: [], body: [] };

  const body = buildBody(msg.parts, renderToolNames);
  const timeline = collectTimeline(msg.parts, renderToolNames, {
    flattenText: false,
    subagentAsThinking: options.subagentAsThinking ?? false,
  });

  return { timeline, body };
};

function buildBody(parts: readonly ChatPart[], renderToolNames: ReadonlySet<string>): BodyEntry[] {
  const renderCalls = parts.filter(
    (p): p is ToolCallPart => p.kind === 'tool-call' && renderToolNames.has(p.name),
  );
  const callByComponentId = buildComponentRegistry(parts, renderCalls);
  const used = new Set<string>();
  const body: BodyEntry[] = [];
  let segCounter = 0;
  const nextKey = (prefix: string) => `${prefix}-${segCounter++}`;

  for (const textPart of parts.filter((p): p is TextPart => p.kind === 'text')) {
    expandTextPart(textPart, callByComponentId, used, body, nextKey);
  }

  for (const call of renderCalls) {
    if (call.status !== 'error') continue;
    body.push({
      kind: 'render-error',
      key: `rerr-${call.toolCallId}`,
      toolName: call.name,
    });
  }

  // Proposal and receipt cards render unconditionally (not gated on a
  // `{{component}}` token), appended after the prose at the end of the turn.
  for (const part of parts) {
    if (part.kind === 'propose-action') {
      body.push({ kind: 'propose-action', key: `pa-${part.proposalId}`, part });
    } else if (part.kind === 'action-executed') {
      body.push({ kind: 'action-executed', key: `ae-${part.executionId}`, part });
    }
  }

  return body;
}

function buildComponentRegistry(
  parts: readonly ChatPart[],
  renderCalls: readonly ToolCallPart[],
): Map<string, ToolCallPart> {
  const resultByCallId = new Map<string, unknown>(
    parts
      .filter((p): p is ToolResultPart => p.kind === 'tool-result')
      .map(p => [p.toolCallId, p.payload]),
  );
  const registry = new Map<string, ToolCallPart>();
  for (const call of renderCalls) {
    const componentId = extractComponentId(resultByCallId.get(call.toolCallId));
    if (componentId !== undefined && !componentId.startsWith('ERROR:')) {
      registry.set(componentId, call);
    }
  }
  // Direct-render components carry `name` + `props` inline with no tool call. Wrap each in a
  // synthetic ToolCallPart keyed by `componentId` so the marker-swap path stays unchanged.
  for (const part of parts) {
    if (part.kind !== 'render-component') continue;
    if (registry.has(part.componentId)) continue;
    registry.set(part.componentId, toSyntheticToolCallPart(part));
  }
  return registry;
}

function toSyntheticToolCallPart(part: RenderComponentPart): ToolCallPart {
  return {
    kind: 'tool-call',
    toolCallId: part.componentId,
    name: part.name,
    args: part.props,
    status: 'done',
    startedAt: 0,
  };
}

function expandTextPart(
  textPart: TextPart,
  callByComponentId: ReadonlyMap<string, ToolCallPart>,
  used: Set<string>,
  body: BodyEntry[],
  nextKey: (prefix: string) => string,
): void {
  const safeContent = textPart.done
    ? textPart.content
    : chompPartialTrailingToken(textPart.content);

  let lastEnd = 0;
  COMPONENT_TOKEN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = COMPONENT_TOKEN_REGEX.exec(safeContent)) !== null) {
    const before = safeContent.slice(lastEnd, match.index);
    if (before.length > 0) {
      body.push({
        kind: 'text',
        key: nextKey(`${textPart.messageId}-t`),
        content: before,
        done: true,
      });
    }
    const componentId = match[1]!;
    const call = callByComponentId.get(componentId);
    if (call && !used.has(componentId)) {
      used.add(componentId);
      body.push({
        kind: 'render-tool',
        key: `${textPart.messageId}-rt-${componentId}`,
        part: call,
      });
    }
    lastEnd = match.index + match[0].length;
  }

  const tail = safeContent.slice(lastEnd);
  if (tail.length > 0) {
    body.push({
      kind: 'text',
      key: nextKey(`${textPart.messageId}-t`),
      content: tail,
      done: textPart.done,
    });
  }
}

function extractComponentId(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const value = (<{ componentId?: unknown }>payload).componentId;
  return typeof value === 'string' ? value : undefined;
}

/**
 * While a text part is still streaming, hide any trailing `{{...` that hasn't closed.
 * Avoids briefly flashing raw token syntax before it resolves to a component.
 */
function chompPartialTrailingToken(text: string): string {
  const lastOpen = text.lastIndexOf('{{');
  if (lastOpen === -1) return text;
  if (text.indexOf('}}', lastOpen) !== -1) return text;
  return text.slice(0, lastOpen);
}

interface CollectContext {
  /** Provenance tag — set when walking inside a flattened `agent-*` sub-agent message. */
  subAgentToolName?: string;
  /** When true, text parts are converted to reasoning events (sub-agent only). */
  flattenText: boolean;
  /** When true, sub-agent inner steps inline into the parent timeline. */
  subagentAsThinking: boolean;
}

function collectTimeline(
  parts: readonly ChatPart[],
  renderToolNames: ReadonlySet<string>,
  context: CollectContext,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const part of parts) {
    if (part.kind === 'reasoning') {
      events.push({
        kind: 'reasoning',
        messageId: part.messageId,
        content: part.content,
        done: part.done,
        ...(context.subAgentToolName !== undefined
          ? { subAgentToolName: context.subAgentToolName }
          : {}),
      });
      continue;
    }
    if (part.kind === 'text' && context.flattenText) {
      events.push({
        kind: 'reasoning',
        messageId: part.messageId,
        content: part.content,
        done: part.done,
        ...(context.subAgentToolName !== undefined
          ? { subAgentToolName: context.subAgentToolName }
          : {}),
      });
      continue;
    }
    if (part.kind !== 'tool-call') continue;
    if (renderToolNames.has(part.name)) continue;
    if (HIDDEN_META_TOOL_NAMES.has(part.name)) continue;

    if (part.name.startsWith(SUB_AGENT_TOOL_NAME_PREFIX)) {
      if (!context.subagentAsThinking) continue;
      for (const subMsg of part.subAgentMessages ?? []) {
        events.push(
          ...collectTimeline(subMsg.parts, renderToolNames, {
            subAgentToolName: part.name,
            flattenText: true,
            subagentAsThinking: true,
          }),
        );
      }
      continue;
    }

    events.push({
      kind: 'tool',
      toolCallId: part.toolCallId,
      name: part.name,
      status: part.status,
      ...(context.subAgentToolName !== undefined
        ? { subAgentToolName: context.subAgentToolName }
        : {}),
    });
  }
  return events;
}
